import { AlertCircle } from "lucide-react";
import { ActionCard } from "./ActionCard";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { cn } from "@/lib/utils";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  actionsTaken?: string[];
  timestamp: Date;
  isError?: boolean;
}

export interface UserAvatarInfo {
  avatarUrl?: string | null;
  avatarColor: string;
  initials: string;
}

interface AssistantMessageProps {
  message: Message;
  userAvatar?: UserAvatarInfo;
}

export function AssistantMessage({ message, userAvatar }: AssistantMessageProps) {
  const isUser = message.role === "user";

  const renderContent = (content: string) => {
    return content.split("\n").map((line, i, arr) => (
      <span key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </span>
    ));
  };

  return (
    <div className={cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row")}>
      {/* Avatar */}
      {isUser ? (
        userAvatar ? (
          <EmployeeAvatar
            avatarUrl={userAvatar.avatarUrl}
            avatarColor={userAvatar.avatarColor}
            initials={userAvatar.initials}
            size="sm"
            className="flex-shrink-0"
          />
        ) : (
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-white text-xs font-semibold">
            U
          </div>
        )
      ) : message.isError ? (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
          <AlertCircle className="h-4 w-4" />
        </div>
      ) : (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full overflow-hidden bg-white shadow-sm border border-border">
          <img src="/besthr.png" alt="Sara" className="h-6 w-6 object-contain" />
        </div>
      )}

      {/* Content */}
      <div className={cn("max-w-[80%] space-y-1", isUser ? "items-end" : "items-start")}>
        <div
          className={cn(
            "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
            isUser
              ? "rounded-tr-sm bg-primary text-primary-foreground"
              : message.isError
              ? "rounded-tl-sm border border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-400"
              : "rounded-tl-sm border bg-card text-foreground shadow-sm",
          )}
        >
          {renderContent(message.content)}
        </div>

        {!isUser && message.actionsTaken && message.actionsTaken.length > 0 && (
          <ActionCard actions={message.actionsTaken} />
        )}

        <p
          className={cn(
            "text-[10px] text-muted-foreground",
            isUser ? "text-right pr-1" : "text-left pl-1",
          )}
        >
          {new Date(message.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
    </div>
  );
}

export function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full overflow-hidden bg-white shadow-sm border border-border">
        <img src="/besthr.png" alt="Sara" className="h-6 w-6 object-contain" />
      </div>
      <div className="rounded-2xl rounded-tl-sm border bg-card px-4 py-3 shadow-sm">
        <div className="flex items-center gap-1">
          <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:0ms]" />
          <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:150ms]" />
          <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:300ms]" />
        </div>
      </div>
    </div>
  );
}
